/**
 * OKS-761 — YouTube URL → video-id parsing + embed/thumbnail URL builders
 * for the creator Learn module inline player (`CreatorModuleYoutubePlayer`).
 *
 * A Learn LINK module arrives as an opaque `link_url` string. This module is
 * the single classifier that decides whether that URL is a *playable YouTube
 * video* (→ inline player) or anything else (→ the existing external-link
 * card, unchanged). Everything here is pure + framework-free so it is trivial
 * to unit-test without loading a real video (TRD §7 — tests assert strings,
 * never network).
 *
 * Scope is YouTube-only by design (Vimeo/other = V2): a non-YouTube host, or
 * a YouTube URL whose id doesn't match the canonical shape, returns `null` so
 * the caller soft-fails to the card (PRD AC — malformed → card, no crash).
 */

/**
 * The canonical YouTube video-id shape: exactly 11 chars from the URL-safe
 * base64 alphabet. This guard is load-bearing (TRD KD-3):
 *
 *   - It is literally what "a valid YouTube id" means — a bare path segment
 *     of any other length/charset is not an id.
 *   - It makes the malformed → card soft-fail a REAL branch, not a formality.
 *   - It stops the OKS-602 test_support fixture (`youtu.be/e2e_mod_#{i}`,
 *     9 chars) from newly rendering a player where the OKS-602 module-progress
 *     E2E expects the link card — no regression to `creator-masterclass-modules.spec.ts`.
 */
const YOUTUBE_ID_RE = /^[A-Za-z0-9_-]{11}$/

/**
 * Hosts we accept as YouTube. Matched case-insensitively against the URL's
 * hostname (with any leading `www.`/`m.`/`music.` stripped). `youtu.be` is the
 * short-link host; the rest resolve `watch`/`shorts` paths.
 */
const WATCH_HOSTS = new Set(["youtube.com", "youtube-nocookie.com"])
const SHORT_HOST = "youtu.be"

/**
 * Parse a URL string to a canonical 11-char YouTube video id, or `null` when
 * the URL is not a recognisable YouTube video link.
 *
 * Recognised shapes (extra query params like `&t=`/`&list=` are ignored):
 *   - `https://www.youtube.com/watch?v=VIDEOID`
 *   - `https://youtu.be/VIDEOID`
 *   - `https://www.youtube.com/shorts/VIDEOID`
 *   - `m.` / `music.` host variants of the above
 *
 * Returns `null` for: non-YouTube hosts (e.g. vimeo.com), a `watch` URL with a
 * missing/empty `v`, a `playlist` URL, and any id that fails the 11-char guard.
 * Malformed/unparseable strings never throw — they return `null` (soft-fail).
 *
 * @param url the raw `link_url` from the Learn module (may be any string)
 * @returns the 11-char video id, or `null`
 */
export function parseYouTubeId(url: string | null | undefined): string | null {
  if (!url) return null

  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    // Not an absolute URL — soft-fail rather than throw.
    return null
  }

  const host = parsed.hostname.toLowerCase().replace(/^(www|m|music)\./, "")

  let candidate: string | null = null

  if (host === SHORT_HOST) {
    // youtu.be/VIDEOID — id is the first (only) path segment.
    candidate = parsed.pathname.split("/").find(Boolean) ?? null
  } else if (WATCH_HOSTS.has(host)) {
    const segments = parsed.pathname.split("/").filter(Boolean)
    if (segments[0] === "shorts" || segments[0] === "embed") {
      // /shorts/VIDEOID or /embed/VIDEOID
      candidate = segments[1] ?? null
    } else {
      // /watch?v=VIDEOID
      candidate = parsed.searchParams.get("v")
    }
  }

  if (candidate && YOUTUBE_ID_RE.test(candidate)) return candidate
  return null
}

/**
 * Build the privacy-friendly no-cookie embed URL for a parsed video id.
 * Uses `youtube-nocookie.com` so no tracking cookies are set until (and only
 * if) the creator activates playback (lite-embed swaps this in on click).
 *
 * @param id an 11-char id already validated by {@link parseYouTubeId}
 */
export function youtubeEmbedUrl(id: string): string {
  return `https://www.youtube-nocookie.com/embed/${id}`
}

/**
 * Build the `hqdefault` thumbnail URL for a parsed video id. `hqdefault`
 * (480×360) is chosen over `maxresdefault` because it is present for every
 * video (maxres 404s on some), so the lite-embed poster never breaks.
 *
 * @param id an 11-char id already validated by {@link parseYouTubeId}
 */
export function youtubeThumbnailUrl(id: string): string {
  return `https://i.ytimg.com/vi/${id}/hqdefault.jpg`
}
